/**
 * Units at the operation boundary (spec 021): arguments are in seconds, % of
 * the stage, 0–1 volume, −1–1 pan and degrees; each operation converts to the
 * stored units (spec 018's document) here.
 */
import type { Ease } from './types';

/** Seconds (decimals allowed) to whole milliseconds. */
export const secToMs = (s: number): number => Math.round(s * 1000);

/** Milliseconds shown as seconds (`1.5s`). */
export const fmtMs = (ms: number | undefined): string => `${Math.round(ms ?? 0) / 1000}s`;

/** Seconds shown as seconds (`1.5s`). */
export const fmtS = (s: number | undefined): string => `${Math.round((s ?? 0) * 1000) / 1000}s`;

/** The ease enum mapped to the GSAP string the PropertiesPanel ease picker writes for each option (D-20). */
export const EASE_TO_GSAP: Readonly<Record<Ease, string>> = {
  linear: 'none',
  easeIn: 'power1.in',
  easeOut: 'power1.out',
  easeInOut: 'power1.inOut',
  strongOut: 'power2.out',
  strongInOut: 'power2.inOut',
  elastic: 'elastic.out',
  bounceOut: 'bounce.out',
};

export const EASES: readonly Ease[] = ['linear', 'easeIn', 'easeOut', 'easeInOut', 'strongOut', 'strongInOut', 'elastic', 'bounceOut'];

/** Ranges the operations check (`out_of_range`), in operation units. */
export const RANGES = {
  /** Sprite entrance/exit duration, seconds. */
  motionDuration: [0, 30],
  /** Sprite delay and hold, seconds. */
  delayHold: [0, 600],
  /** Scene transition duration, seconds. */
  transitionDuration: [0, 10],
  /** Dialog start, sound start/end, seconds. */
  sceneTime: [0, 3600],
  /** Dialog line hold, seconds. */
  lineDuration: [0.1, 120],
  /** Sound fade in/out, seconds. */
  fade: [0, 60],
  volume: [0, 1],
  pan: [-1, 1],
  scale: [0.05, 20],
  /** Rotation, degrees. */
  rotation: [-360, 360],
  /** Sprite box side, canvas px. */
  size: [1, 4000],
  /** Hinge, 0–1 of the box on each axis. */
  hinge: [0, 1],
  /** Slide offset, % of the stage along the slide. */
  offset: [0, 500],
  /** Choice reveal before the end, seconds. */
  showBeforeEnd: [0, 600],
  /** Name lengths, characters. */
  name: [1, 255],
  lineText: [0, 1000],
} as const satisfies Record<string, readonly [number, number]>;
